import { useGetBuilding } from "api/hooks/allHooks";
import type { CrremDashboardMetricBlock } from "features/crrem-pathway-chart/model/types";
import type { CrremPathwayScale } from "features/crrem-pathway-chart/model/types";
import { useCrremDashboardScenarios } from "features/crrem-pathway-chart/hooks/useCrremDashboardScenarios";
import { useCrremPathwaySettings } from "features/crrem-pathway-chart/hooks/useCrremPathwaySettings";
import { parseBuildingAreas } from "features/crrem-pathway-chart/lib/parseBuildingAreas";
import { pickDemandFromDashboard } from "features/crrem-pathway-chart/lib/parseCrremDashboardApi";
import { resolveAreaDivisor, scaleChartValue } from "features/crrem-pathway-chart/lib/scaleChartValues";
import { CRREM_PATHWAY_SCALE } from "features/crrem-pathway-chart/model/constants";
import { useMemo } from "react";
import { m2 } from "utils/textSymbols";

import type { EnergyAnalysisDimension, EnergyAnalysisRow, EnergyAnalysisYearRange } from "../model/types";

import { collectYears } from "../lib/aggregate";
import { ENERGY_ANALYSIS_DIMENSION } from "../model/constants";
import { useCrremSettings } from "./useCrremSettings";
import { useEnergyAnalysisViewModel } from "./useEnergyAnalysisViewModel";

const resolveYearRange = (
  years: number[],
  yearRange?: EnergyAnalysisYearRange
): EnergyAnalysisYearRange | undefined => {
  if (yearRange) return yearRange;
  const from = years[0];
  const to = years[years.length - 1];
  if (from === undefined || to === undefined) return undefined;
  return [from, to];
};

export const useEnergyAnalysisDashboard = (
  projectId: string,
  dimension: EnergyAnalysisDimension,
  fallbackUnit: string,
  yearRange?: EnergyAnalysisYearRange,
  snapshotYear?: number
) => {
  const { saveSettings, settings } = useCrremSettings(projectId);
  const { config, updateConfig } = useCrremPathwaySettings(projectId);
  const dashboardQuery = useCrremDashboardScenarios(projectId);
  const buildingQuery = useGetBuilding(projectId);
  const areas = parseBuildingAreas(buildingQuery.data);
  const divisor = resolveAreaDivisor(config.scale, areas);
  const scaleRows = (rows: EnergyAnalysisRow[]) =>
    rows.map((row) => ({ ...row, value: scaleChartValue(row.value, divisor) }));
  const scaleMetric = (metric: CrremDashboardMetricBlock): CrremDashboardMetricBlock => ({
    ...metric,
    baseline: scaleRows(metric.baseline),
    corrected: scaleRows(metric.corrected),
    rows: scaleRows(metric.rows),
    unit: divisor ? `${metric.unit}/${m2}` : metric.unit,
  });
  const dashboardPayload = useMemo(
    () =>
      dashboardQuery.data
        ? {
            ...dashboardQuery.data,
            trends: dashboardQuery.data.trends.map((trend) => ({
              ...trend,
              scenarios: trend.scenarios.map((scenario) => ({
                ...scenario,
                co2: scaleMetric(scenario.co2),
                energy: scaleMetric(scenario.energy),
              })),
            })),
          }
        : undefined,
    [dashboardQuery.data, divisor]
  );
  const demand = pickDemandFromDashboard(dashboardPayload, settings, fallbackUnit);
  const rows = demand.rows;
  const availableYears = collectYears(rows);
  const resolvedYearRange = resolveYearRange(availableYears, yearRange);
  const filteredRows =
    resolvedYearRange === undefined
      ? rows
      : rows.filter((row) => row.year >= resolvedYearRange[0] && row.year <= resolvedYearRange[1]);
  const unit = demand.unit;
  const viewDimension =
    dimension === ENERGY_ANALYSIS_DIMENSION.mix
      ? ENERGY_ANALYSIS_DIMENSION.mix
      : dimension === ENERGY_ANALYSIS_DIMENSION.utility
        ? ENERGY_ANALYSIS_DIMENSION.utility
        : ENERGY_ANALYSIS_DIMENSION.useType;
  const viewModel = useEnergyAnalysisViewModel(
    filteredRows,
    viewDimension,
    unit,
    snapshotYear ?? demand.lastReportedYear
  );
  return {
    availableYears,
    dashboardPayload,
    filteredRows,
    isEmpty: !dashboardQuery.isLoading && filteredRows.length === 0,
    isLoading:
      dashboardQuery.isLoading ||
      (config.scale !== CRREM_PATHWAY_SCALE.none && buildingQuery.isLoading),
    saveSettings,
    settings,
    settingsError: dashboardQuery.isError,
    scale: config.scale,
    setScale: (scale: CrremPathwayScale) => updateConfig({ scale }),
    viewModel,
    yearRange: resolvedYearRange,
  };
};
